interface Array<T> {
	remove(o: T): T[]
	removeAll(o: T): T[]
	pickRandom(mt?): T
	isUniqueOn(identify: (t: T) => any): boolean
	pushUnique(o: T)
	add(o: T | T[])
	first(): T
}

interface Math {
	clamp(num: number, lowerBound?: number, upperBound?: number): number
	getRandomFloat(min: number, max: number): number
	/**
	 * 
	 * @param min minimum int in the range (inclusive)
	 * @param max maximum int in the range (inclusive)
	 * @returns an integer between min and max
	 */
	getRandomInt(min: number, max: number): number
	/**
	 * 
	 * @param percent The percent that the roll must be under to return true
	 */
	isPercentChance(percent: number): boolean
	powSlope(value: number, power: number, slope: number): number
	/**
	 * Linear interpolate (lerp) between `a` and `b` based on the percentage passed for `t`
	 * @param a Value at the start of the lerp where `t = 0`
	 * @param b Value at the end of the lerp where `t = 1`
	 * @param t Percentage between `a` and `b`
	 */
	lerp(a: number, b: number, t: number): number
	/**
	 * Identical to Math.lerp, but `t` is clamped to a value between 0 and 1
	 * @param a Value at the start of the lerp where `t = 0`
	 * @param b Value at the end of the lerp where `t = 1`
	 * @param t Percentage between `a` and `b` (clamped to a value between 0 and 1)
	 */
	clerp(a: number, b: number, t: number): number
	distanceBetweenPoints(sourceX: number, sourceY: number, destX: number, destY: number): number
}

interface String {
	toProperCase(): string
	toCamelCase(): string
	toSnakeCase(): string
	toKababCase(): string
	commafy(): string
	replaceAllSLOW(search: string, replace: string): string
}

interface Number {
	toFixedIfFloat(places: number): string
	toOrdinalNotation(): string
	between(lowerBound: number, upperBound: number, exclusive?: boolean): boolean
}

interface Map<K, V> {
	getWithFallback(key: K, fallback: V): V
}

Map.prototype.getWithFallback = function (key: any, fallback: any) {
	if (!this.has(key)) {
		this.set(key, fallback)
	}
	return this.get(key)
}

if (!('toJSON' in Error.prototype)) {
	Object.defineProperty(Error.prototype, 'toJSON', {
		value() {
			const alt = {}

			Object.getOwnPropertyNames(this).forEach(function (key) {
				alt[key] = this[key]
			}, this)

			return alt
		},
		configurable: true,
		writable: true,
	})
}

Array.prototype.remove = function (thingToRemove) {
	const indexOfItemToRemove = this.indexOf(thingToRemove)
	if (indexOfItemToRemove > -1) {
		return this.splice(indexOfItemToRemove, 1)
	} else {
		return this
	}
}

Array.prototype.removeAll = function (thingToRemove) {
	let indexOfItemToRemove = this.indexOf(thingToRemove)
	while (indexOfItemToRemove > -1) {
		this.splice(indexOfItemToRemove, 1)
		indexOfItemToRemove = this.indexOf(thingToRemove)
	}
	return this
}

Array.prototype.pickRandom = function (mt?) {
	if (!this.length) {
		return null
	}
	const r = mt ? mt.random() : Math.random()
	const idx = Math.floor(r * this.length)
	if (idx >= this.length) {
		throw new Error(`bad index ${idx} >= array length`)
	}
	return this[idx]
}

Array.prototype.isUniqueOn = function (identify: (element: any) => boolean): boolean {
	const tmpArr = []
	for (let i = 0; i < this.length; i++) {
		const obj = this[i]
		const id = identify(obj)
		if (tmpArr.indexOf(id) < 0) {
			tmpArr.push(id)
		} else {
			return false // Duplicate found
		}
	}
	return true // No duplicate found
}

Array.prototype.pushUnique = function (o) {
	if (this.indexOf(o) === -1) {
		this.push(o)
	}
}

//concat makes a new array, so here is this garbage
Array.prototype.add = function (o) {
	if (Array.isArray(o)) {
		this.push(...o)
	} else {
		this.push(o)
	}
}

Array.prototype.first = function () {
	return this.length ? this[0] : undefined
}

Math.clamp = function (num: number, lowerBound: number = 0, upperBound: number = 1): number {
	return Math.min(upperBound, Math.max(num, lowerBound))
}

Math.distanceBetweenPoints = (sourceX: number, sourceY: number, destX: number, destY: number): number => {
	return Math.hypot(destX - sourceX, destY - sourceY)
}

Math.getRandomFloat = function (min: number, max: number): number {
	return Math.random() * (max - min) + min
}

Math.getRandomInt = function (min: number, max: number): number {
	min = Math.ceil(min)
	max = Math.floor(max)
	return Math.floor(Math.random() * (max - min + 1)) + min
}

Math.isPercentChance = function (percent: number): boolean {
	return Math.random() <= percent / 100
}

Math.lerp = function (a, b, t): number {
	return a * (1.0 - t) + b * t
}

Math.clerp = function (a, b, t): number {
	t = Math.clamp(t, 0, 1)
	return a * (1.0 - t) + b * t
}

Math.powSlope = function (value: number, power: number, slope: number): number {
	return Math.pow(value, power) * slope
}

String.prototype.toProperCase = function () {
	return this.replace(/\w\S*/g, function (frag: string) {
		return frag.charAt(0).toUpperCase() + frag.substr(1).toLowerCase()
	})
}

String.prototype.toCamelCase = function () {
	return this.replace(/\s(.)/g, function ($1) {
		return $1.toUpperCase()
	})
		.replace(/\s/g, '')
		.replace(/^(.)/, function ($1) {
			return $1.toLowerCase()
		})
}

String.prototype.toSnakeCase = function () {
	return this.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
		.map((x) => x.toLowerCase())
		.join('_')
}

String.prototype.commafy = function () {
	return this.replace(/(^|[^\w.])(\d{4,})/g, function ($0, $1, $2) {
		return $1 + $2.replace(/\d(?=(?:\d\d\d)+(?!\d))/g, '$&,')
	})
}

String.prototype.replaceAllSLOW = function (search: string, replace: string) {
	return this.split(search).join(replace)
}

String.prototype.toKababCase = function () {
	const match = this.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
	if (match) {
		return match.map((x) => x.toLowerCase()).join('-')
	}
	return this
}

Number.prototype.toOrdinalNotation = function (): string {
	return getOrdinal(this)
}

Number.prototype.toFixedIfFloat = function (places) {
	if (this % 1 !== 0) {
		return this.toFixed(places)
	} else {
		return this.toString()
	}
}

Number.prototype.between = function (lowerBound: number, upperBound: number, exclusive?: boolean): boolean {
	if (exclusive) {
		return this > lowerBound && this < upperBound
	}
	return this >= lowerBound && this <= upperBound
}


function getOrdinal(i) {
	const j = i % 10
	const k = i % 100
	if (j == 1 && k != 11) {
		return i + "st"
	}
	if (j == 2 && k != 12) {
		return i + "nd"
	}
	if (j == 3 && k != 13) {
		return i + "rd"
	}
	return i + "th"
}

